import React, { useState } from 'react';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import {
    CustomInput,
    CustomTextArea,
    StyledCodeMirror,
} from 'src/domains/layouts/webview/ConfigurationManager/components/customFormElements/customFormElements.style';
import { observer } from 'src/utils/mobx-react';
import { FieldProps, WidgetProps } from '@rjsf/utils';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { ServiceSettingsFormState } from 'src/domains/layouts/webview/ConfigurationManager/serviceSettingsForm/serviceSettingsForm.state';
import { useCommon } from 'src/AppState/Common';
import { valueExistInCustomValuePaths } from 'src/domains/layouts/webview/ConfigurationManager/utils';
import { action, makeObservable, observable } from 'src_common/common/mobx-wrapper';
import { ApiGetSettingsResponse200Type } from 'src/api_openapi/v2/settingsService/apiGetSettings';

// CustomFields ----------
export const CustomTitleField = (props: FieldProps): JSX.Element => {
    console.log('props CustomTitleField', props);
    return <h2 style={{ color: 'yellow' }}>{props.title}</h2>;
};

class CustomStringState {
    @observable public isChecked: boolean = true;

    public constructor(
        private readonly props: CustomStringPropsType,
        private readonly data: ApiGetSettingsResponse200Type['data']
    ) {
        this.isChecked = valueExistInCustomValuePaths(this.props.idSchema.$id, this.data.customValuePaths) === false;

        makeObservable(this);
    }

    @action public onToggleChecked = (): void => {
        this.isChecked = !this.isChecked;
    };
}

interface CustomStringPropsType extends FieldProps {
    currentServiceId: string;
}

export const CustomString = observer('CustomString', (props: CustomStringPropsType): JSX.Element | null => {
    // console.log('props CustomString', props);
    const common = useCommon();
    const serviceSettingsFormState = ServiceSettingsFormState.get(common, props.currentServiceId);
    const data = serviceSettingsFormState.getReady();
    if (data === null) {
        return null;
    }
    const [state] = useState(() => new CustomStringState(props, data));
    const { schema } = props;
    const typeOfForm = schema.type;
    const inputOptions = schema.enum;
    const { format } = schema;
    // const { widgets, formContext, schemaUtils, globalUiOptions } = registry;

    const hasError = props.rawErrors !== undefined;
    const setDefaultValueCheckbox = (
        <Checkbox
            labelSide='right'
            onChange={() => {
                // serviceSettingsFormState.setShouldSubmitFlag(true);
                state.onToggleChecked();
                if (state.isChecked) {
                    props.onChange(null);
                    serviceSettingsFormState.addPath(props.idSchema.$id);
                } else {
                    props.onChange(props.formData);
                    serviceSettingsFormState.removePath(props.idSchema.$id);
                }
            }}
            isChecked={state.isChecked}
            isDisabled={false}
        >
            <p>Use Default Value</p>
        </Checkbox>
    );

    if (typeOfForm === 'string' && inputOptions === undefined) {
        if (format === 'email') {
            return (
                <div>
                    <CustomInput
                        hasError={hasError}
                        type='email'
                        id={props.idSchema.$id}
                        placeholder={props.schema.title}
                        value={props.formData}
                        onChange={(event) => props.onChange(event.target.value)}
                        disabled={state.isChecked}
                    />
                    {setDefaultValueCheckbox}
                </div>
            );
        }

        if (format === 'date') {
            return (
                <div>
                    <CustomInput
                        hasError={hasError}
                        type='date'
                        id={props.idSchema.$id}
                        placeholder={props.schema.title}
                        value={props.formData}
                        onChange={(event) => props.onChange(event.target.value)}
                        disabled={state.isChecked}
                    />
                    {setDefaultValueCheckbox}
                </div>
            );
        }

        if (format === 'password') {
            return (
                <div>
                    <CustomInput
                        hasError={hasError}
                        type='password'
                        id={props.idSchema.$id}
                        placeholder={props.schema.title}
                        value={schema['x-secret'] === undefined ? props.formData : undefined}
                        onChange={(event) => props.onChange(event.target.value)}
                        disabled={state.isChecked}
                    />
                    {setDefaultValueCheckbox}
                </div>
            );
        }
        if (format === 'textarea') {
            const codeFormat = schema['x-format'] ?? 'html';
            // In your CustomString component:
            if (schema['x-format'] !== undefined) {
                return (
                    <StyledCodeMirror>
                        <CodeMirror
                            value={props.formData}
                            height='450px'
                            extensions={[codeFormat === 'html' ? html() : css()]}
                            onChange={(value) => props.onChange(value)}
                            theme='light'
                        />
                        {setDefaultValueCheckbox}
                    </StyledCodeMirror>
                );
            }
            return (
                <CustomTextArea
                    id={props.idSchema.$id}
                    value={props.formData}
                    onChange={(event) => props.onChange(event.target.value)}
                />
            );
        }
        return (
            <div>
                <CustomInput
                    hasError={hasError}
                    id={props.idSchema.$id}
                    placeholder={props.schema.title}
                    value={props.formData}
                    onChange={(event) => props.onChange(event.target.value)}
                    disabled={state.isChecked}
                ></CustomInput>
                {setDefaultValueCheckbox}
            </div>
        );
    }

    if (inputOptions !== undefined) {
        return (
            <div>
                <select
                    id={props.idSchema.$id}
                    value={props.formData}
                    onChange={(event) => props.onChange(event.target.value)}
                    disabled={state.isChecked}
                >
                    {inputOptions.map((option) => {
                        if (typeof option === 'string') {
                            return (
                                <option
                                    key={option}
                                    value={option}
                                >
                                    {option}
                                </option>
                            );
                        }
                    })}
                </select>
                {setDefaultValueCheckbox}
            </div>
        );
    }

    console.error('Unknown type of form', props.schema);
    return <div>Unknown type of form</div>;
});

export const CustomDescriptionField = (props: FieldProps): JSX.Element => {
    console.log('props CustomDescriptionField', props);
    return <p style={{ color: 'green' }}>{props.description}</p>;
};

//  CustomWidgets ----------
export const CustomCheckbox = (props: WidgetProps): JSX.Element => {
    // console.log('props CustomCheckbox', props);
    return (
        <Checkbox
            labelSide='right'
            onChange={() => props.onChange(props.value === false)}
            isChecked={props.value}
            isDisabled={props.disabled ?? false}
        >
            <p>{props.schema.description}</p>
        </Checkbox>
    );
};
